// `grid callers` — who may call an app you own (Decision 051).
//
// The callee side of App Connection. `list` shows every caller grant on an
// app — approved callers, requests pending your approval, and revoked or
// rejected history. `approve` / `revoke` are the callee admin's controls;
// both resolve a caller address (or a raw grant id) to the grant and hit
// the matching v2 route. Revoke is durable: a caller's re-plug files a NEW
// request instead of quietly restoring access (caller-deps.ts, founder call
// 2026-07-07) — the copy here says so.

import { Command } from 'commander';
import type { CallerGrant } from '../../../../shared/src/v1-types.js';
import type { IV2Client } from '../../api/v2-client.js';
import { getV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { Output } from '../../output.js';
import { addOutputOption, isStructuredOutput } from '../../services/output-format.js';
import { CloudGridError } from '../../utils.js';
import {
  fetchEntityNames,
  lookupCallEntity,
  matchGrants,
  renderGrantsTable,
  resolveAppTarget,
  serializeGrant,
  sortGrants,
  counterpartId,
} from './shared.js';

interface ListOpts {
  json?: boolean;
  output?: string;
}

interface ActionOpts {
  json?: boolean;
  reason?: string;
}

/** Load the app (callee) and its inbound grants + the grid's name map. */
async function loadInbound(
  client: IV2Client,
  appArg: string | undefined,
): Promise<{
  app: { entity_id: string; name: string };
  grants: CallerGrant[];
  names: Map<string, string>;
}> {
  const app = await lookupCallEntity(client, resolveAppTarget(appArg));
  const [{ grants }, names] = await Promise.all([
    client.get<{ grants: CallerGrant[] }>(
      `/api/v2/entities/${encodeURIComponent(app.entity_id)}/caller-grants`,
    ),
    fetchEntityNames(client),
  ]);
  return { app: { entity_id: app.entity_id, name: app.name }, grants, names };
}

// ── list ──────────────────────────────────────────────────────────────────

export async function runCallersList(
  client: IV2Client,
  appArg: string | undefined,
  opts: ListOpts,
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  const { app, grants, names } = await loadInbound(client, appArg);
  const rows = sortGrants(grants, names, 'inbound');

  out.json(() => ({
    app,
    total: rows.length,
    grants: rows.map((g) => serializeGrant(g, names, 'inbound')),
  }));

  out.human(() => {
    if (rows.length === 0) {
      return `No callers. An app requests access by declaring calls: ${app.name} in its cloudgrid.yaml and plugging.`;
    }
    const table = renderGrantsTable(rows, names, 'inbound', out.isWide);
    const pending = rows.find((g) => g.status === 'requested');
    if (pending) {
      const caller = names.get(pending.from_entity_id) ?? pending.from_entity_id;
      return `${table}\n\nApprove a pending caller: grid callers approve ${app.name} ${caller}`;
    }
    return table;
  });
}

// ── approve ───────────────────────────────────────────────────────────────

export async function runCallersApprove(
  client: IV2Client,
  appArg: string,
  callerArg: string,
  opts: ActionOpts,
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false });
  const { app, grants, names } = await loadInbound(client, appArg);
  const matched = matchGrants(grants, callerArg, names, 'inbound');
  if (matched.length === 0) {
    throw new CloudGridError(
      `No request from ${callerArg}. An app requests access by declaring calls: ${app.name} in its cloudgrid.yaml and plugging.`,
    );
  }
  const callerName = names.get(counterpartId(matched[0], 'inbound')) ?? callerArg;

  const pending = matched.find((g) => g.status === 'requested');
  if (!pending) {
    if (matched.some((g) => g.status === 'approved')) {
      out.json(() => ({ grant: serializeGrant(matched.find((g) => g.status === 'approved')!, names, 'inbound') }));
      out.human(() => `${callerName} is already approved to call ${app.name}.`);
      return;
    }
    // Terminal only — the durable revoke means a fresh request must be filed.
    throw new CloudGridError(
      `The grant for ${callerName} was ${matched[0].status}. A new request appears here when ${callerName} re-plugs with calls: ${app.name} in its cloudgrid.yaml.`,
    );
  }

  const { grant } = await client.post<{ grant: CallerGrant }>(
    `/api/v2/entities/${encodeURIComponent(app.entity_id)}/caller-grants/${encodeURIComponent(pending.grant_id)}/approve`,
    {},
  );

  out.json(() => ({ grant: serializeGrant(grant, names, 'inbound') }));
  out.human(() => `Approved. ${callerName} can now call ${app.name}.`);
}

// ── revoke ────────────────────────────────────────────────────────────────

export async function runCallersRevoke(
  client: IV2Client,
  appArg: string,
  callerArg: string,
  opts: ActionOpts,
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false });
  const { app, grants, names } = await loadInbound(client, appArg);
  const matched = matchGrants(grants, callerArg, names, 'inbound');
  if (matched.length === 0) {
    throw new CloudGridError(
      `No grant from ${callerArg} to ${app.name}. Run grid callers list ${app.name} to see who calls it.`,
    );
  }
  const callerName = names.get(counterpartId(matched[0], 'inbound')) ?? callerArg;

  // Revoking an approved grant cuts access; revoking a pending one declines
  // the request. Prefer the approved grant when both exist.
  const active =
    matched.find((g) => g.status === 'approved') ??
    matched.find((g) => g.status === 'requested');
  if (!active) {
    out.json(() => ({ grant: serializeGrant(matched[0], names, 'inbound') }));
    out.human(() => `Nothing to revoke — ${callerName} already can't call ${app.name}.`);
    return;
  }
  const declining = active.status === 'requested';

  const { grant } = await client.post<{ grant: CallerGrant }>(
    `/api/v2/entities/${encodeURIComponent(app.entity_id)}/caller-grants/${encodeURIComponent(active.grant_id)}/revoke`,
    opts.reason ? { reason: opts.reason } : {},
  );

  out.json(() => ({ grant: serializeGrant(grant, names, 'inbound') }));
  out.human(() =>
    declining
      ? `Declined. The request from ${callerName} is closed; a re-plug files a new one for you to approve.`
      : `Revoked. ${callerName} can't call ${app.name} anymore. A re-plug won't restore access — approve the new request if you change your mind.`,
  );
}

// ── Commander wiring ──────────────────────────────────────────────────────

const listCommand = addOutputOption(
  new Command('list')
    .description('Show who may call an app you own')
    .argument('[app]', 'App address (defaults to the entity linked to this folder)')
    .option('--json', 'machine-readable output (alias of -o json)'),
).action(async (app: string | undefined, opts: ListOpts) => {
  try {
    const client = await getV2Client();
    await runCallersList(client, app, opts);
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});

const approveCommand = new Command('approve')
  .description('Approve a pending caller (callee admin only)')
  .argument('<app>', 'The app being called (an app you administer)')
  .argument('<caller>', 'Caller address or grant id')
  .option('--json', 'machine-readable output')
  .action(async (app: string, caller: string, opts: ActionOpts) => {
    try {
      const client = await getV2Client();
      await runCallersApprove(client, app, caller, opts);
    } catch (err) {
      await handleV2Error(err, { json: !!opts.json });
    }
  });

const revokeCommand = new Command('revoke')
  .description("Revoke a caller's access (callee admin only; a re-plug won't restore it)")
  .argument('<app>', 'The app being called (an app you administer)')
  .argument('<caller>', 'Caller address or grant id')
  .option('--reason <text>', 'Reason recorded on the grant')
  .option('--json', 'machine-readable output')
  .action(async (app: string, caller: string, opts: ActionOpts) => {
    try {
      const client = await getV2Client();
      await runCallersRevoke(client, app, caller, opts);
    } catch (err) {
      await handleV2Error(err, { json: !!opts.json });
    }
  });

export const callersCommand = new Command('callers')
  .description('Who may call an app you own: list, approve, revoke')
  .addCommand(listCommand)
  .addCommand(approveCommand)
  .addCommand(revokeCommand);
